/**
 * M1-24 — the business profile (§7.2, §7.21, §17.5).
 *
 * The receipt identity fields are what a customer reads at the top of a slip
 * of paper, so they belong to the owner. Two things shape this screen.
 *
 * **Changes reach the till at its next licence check, not immediately.** §7.6
 * has the device cache this at each check, and there is no push. An owner who
 * corrects a paybill and watches the wrong one print for another few hours
 * will conclude the save did not work, so the screen says so up front rather
 * than leaving them to find out.
 *
 * **The receipt name defaults to the business name.** Asking for both with no
 * explanation reads like a mistake; the hint says why the two can differ, and
 * the preview shows what the difference actually does.
 */

import { useEffect, useState, type FormEvent } from 'react'

import { api, type Business, type BusinessEdit } from '../api.js'
import { Alert, Card, Field, Rows, Row } from '../components/ui.js'
import { messageFor } from './Register.js'

/** The §17.5 fields, described once so the form and the preview agree. */
const RECEIPT_FIELDS = [
  {
    key: 'businessName',
    label: 'Name on the receipt',
    max: 200,
    hint: 'Defaults to your business name. Set it if the shopfront name differs from the registered one.',
  },
  {
    key: 'locationLine',
    label: 'Location',
    max: 200,
    hint: 'Street and town, as a customer would say it.',
  },
  { key: 'paybill', label: 'Paybill or till number', max: 20, hint: undefined },
  { key: 'accountNumber', label: 'Account number', max: 40, hint: 'If your paybill needs one.' },
  { key: 'phone', label: 'Phone', max: 30, hint: 'For a customer coming back about a purchase.' },
  {
    key: 'footer',
    label: 'Footer line',
    max: 300,
    hint: 'Printed last. A thank-you, or your returns policy.',
  },
] as const

type ReceiptKey = (typeof RECEIPT_FIELDS)[number]['key']

/** What the top of a printed slip will look like, from what is captured now. */
function ReceiptPreview({ business }: { business: Business }) {
  const r = business.receipt
  const name = r.businessName ?? business.name
  const pay = [r.paybill && `Paybill ${r.paybill}`, r.accountNumber && `Acc ${r.accountNumber}`]
    .filter(Boolean)
    .join('  ·  ')

  return (
    <pre className="receipt" aria-label="Receipt preview">
      {name.toUpperCase()}
      {r.locationLine ? `\n${r.locationLine}` : ''}
      {r.phone ? `\n${r.phone}` : ''}
      {pay ? `\n${pay}` : ''}
      {'\n\n--------------------------------\n'}
      {'1 x Brookside Milk 500ml    60.00\n'}
      {'--------------------------------\n'}
      {'TOTAL                       60.00'}
      {r.footer ? `\n\n${r.footer}` : ''}
    </pre>
  )
}

export function Settings() {
  const [business, setBusiness] = useState<Business | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    void api
      .business()
      .then(setBusiness)
      .catch((err: unknown) => setError(messageFor(err)))
  }, [])

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = new FormData(event.currentTarget)
    setBusy(true)
    setError(null)
    setSaved(false)

    // An emptied box sends `null`, which clears the field. Sending `undefined`
    // would mean "leave it alone" and make a filled field impossible to empty.
    const receipt: BusinessEdit['receipt'] = {}
    for (const field of RECEIPT_FIELDS) {
      const value = String(form.get(field.key) ?? '').trim()
      receipt[field.key] = value === '' ? null : value
    }

    try {
      setBusiness(
        await api.updateBusiness({ name: String(form.get('name') ?? '').trim(), receipt }),
      )
      setSaved(true)
    } catch (err) {
      setError(messageFor(err))
    } finally {
      setBusy(false)
    }
  }

  if (error && !business) return <Alert>{error}</Alert>
  if (!business) return <p className="empty">Loading…</p>

  return (
    <>
      {error ? <Alert>{error}</Alert> : null}
      {saved ? (
        <Alert tone="ok">
          Saved. Your till will pick this up at its next licence check — within a few hours, or
          straight away if you restart it.
        </Alert>
      ) : null}

      <form onSubmit={(event) => void save(event)}>
        <Card
          title="Business"
          note="Your name for this account. The name printed on receipts is set below and can differ."
        >
          <Field label="Business name">
            <input
              name="name"
              required
              maxLength={200}
              defaultValue={business.name}
              autoComplete="organization"
            />
          </Field>
          <Rows>
            <Row label="Account email">{business.email ?? '—'}</Row>
          </Rows>
        </Card>

        <Card
          title="Receipt details"
          note="Printed at the top of every receipt. Changes reach the till at its next licence check, so a correction made now appears within a few hours rather than instantly."
        >
          {RECEIPT_FIELDS.map((field) => (
            <Field key={field.key} label={field.label} hint={field.hint}>
              <input
                name={field.key}
                maxLength={field.max}
                defaultValue={business.receipt[field.key as ReceiptKey] ?? ''}
                {...(field.key === 'phone' ? { type: 'tel', autoComplete: 'tel' } : {})}
              />
            </Field>
          ))}

          <div className="actions">
            <button type="submit" className="primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save receipt details'}
            </button>
          </div>

          <p className="card__note" style={{ margin: '0.7rem 0 0' }}>
            Leave a box empty to remove that line from the receipt.
          </p>
        </Card>
      </form>

      <Card
        title="Receipt preview"
        note="How the top of a printed slip will read. The till prints the lines you have filled in and skips the rest."
      >
        <ReceiptPreview business={business} />
      </Card>

      <Card title="Region" note="Used for dates, times and money throughout the till and the receipt.">
        <Rows>
          <Row label="Currency">{business.currency}</Row>
          <Row label="Timezone">{business.timezone}</Row>
          <Row label="Locale">{business.locale}</Row>
        </Rows>
      </Card>
    </>
  )
}
